/**
 * A legend's rows: a manual legend lists its items; a layers legend is made
 * by the print engine from the colors its view actually draws (see
 * `drawingLegendRows` in print-engine) — one row per color, named after the
 * layer when only one layer draws in it, else left for the sheet to name.
 * Each row has a stable key, under which a sheet stores its own text for it
 * (SheetDoc.legendLabels).
 */

import type { PrintColor } from './color';
import type { PlanLayerCategory } from './sheet';
import type { LegendElement } from './spec';

/** Layer names a legend shows until the sheet names the row (English; the sheet's text replaces it). */
export const PLAN_LAYER_LEGEND_NAMES: Readonly<Record<PlanLayerCategory, string>> = {
  Walls: 'Walls',
  Openings: 'Openings',
  Outlines: 'Outlines',
  Objects: 'Objects',
  Dimensions: 'Dimensions',
  Rooms: 'Rooms',
  RoomTags: 'Room tags',
  Polylines: 'Lines',
  Polygons: 'Areas',
  Points: 'Points',
  Beams: 'Beams',
  Columns: 'Columns',
  Labels: 'Labels',
};

export interface LegendRow {
  /** Stable per legend: a manual item's index, a layers row's color (CSS). */
  key: string;
  /** Text shown until the sheet sets its own (may contain placeholders for manual items; empty: a color several layers share). */
  defaultLabel: string;
  color: PrintColor;
  /** A pen line (drawn lines and text) or a filled swatch (filled areas, manual items). */
  swatch: 'line' | 'fill';
  /** Line weight of the swatch (the heaviest line drawn in the color). */
  weightMm: number;
}

/** A manual legend's rows: its items, keyed by index. */
export function manualLegendRows(element: Pick<LegendElement, 'items'>): LegendRow[] {
  return element.items.map((item, i) => ({ key: String(i), defaultLabel: item.label, color: item.color, swatch: 'fill', weightMm: 0 }));
}
